import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ChevronRight, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import VehicleCard from "@/components/VehicleCard";
import api from "@/lib/api";
import { segmentLabel, buildWhatsAppUrl } from "@/lib/site";
import { useSettings } from "@/context/SettingsContext";

const WhatsAppIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487 2.981 1.287 2.981.858 3.518.804.537-.054 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" /></svg>
);

function nowDate() {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 10);
}

function ReservationForm({ vehicle }) {
  const { settings } = useSettings();
  const minDate = nowDate();
  const [f, setF] = useState({ name: "", phone: "", pickup_date: "", return_date: "", note: "", kvkk: false });
  const [err, setErr] = useState({});
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    const er = {};
    if (f.name.trim().length < 3) er.name = "Ad soyad en az 3 karakter olmalı";
    if (!f.phone.trim()) er.phone = "Telefon gerekli";
    if (!f.pickup_date) er.pickup_date = "Alış tarihi gerekli";
    if (f.pickup_date && f.pickup_date < minDate) er.pickup_date = "Geçmiş tarih seçilemez";
    if (!f.return_date) er.return_date = "İade tarihi gerekli";
    if (f.pickup_date && f.return_date && f.return_date <= f.pickup_date) er.return_date = "İade, alıştan sonra olmalı";
    if (!f.kvkk) er.kvkk = "KVKK metnini onaylayın";
    setErr(er);
    if (Object.keys(er).length) return;

    const msg = `Merhaba VoyaCar Rental, ${vehicle.name} kiralamak istiyorum.\nAd Soyad: ${f.name}\nTelefon: ${f.phone}\nAlış: ${f.pickup_date}\nİade: ${f.return_date}${f.note ? `\nNot: ${f.note}` : ""}`;
    const url = buildWhatsAppUrl(settings.whatsapp, msg);
    const win = window.open(url, "_blank");
    if (!win) window.location.href = url;
    toast.success("WhatsApp talebiniz hazırlandı.");
  };

  return (
    <form onSubmit={submit} className="bg-white rounded-2xl border border-neutral-200 p-6 shadow-sm" data-testid="detail-reservation-form">
      <h3 className="text-lg font-bold text-[#171717]">Hemen Rezervasyon Talebi</h3>
      <p className="text-sm text-neutral-500 mt-1 mb-4">Formu doldurun, WhatsApp'tan size dönelim.</p>
      <div className="space-y-3">
        <div>
          <Label className="text-sm mb-1 block">Ad Soyad *</Label>
          <Input value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="rf-name" />
          {err.name && <p className="text-xs text-[#A11016] mt-1">{err.name}</p>}
        </div>
        <div>
          <Label className="text-sm mb-1 block">Telefon *</Label>
          <Input value={f.phone} onChange={(e) => set("phone", e.target.value)} placeholder="05xx xxx xx xx" data-testid="rf-phone" />
          {err.phone && <p className="text-xs text-[#A11016] mt-1">{err.phone}</p>}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-sm mb-1 block">Alış Tarihi *</Label>
            <Input type="date" min={minDate} value={f.pickup_date} onChange={(e) => set("pickup_date", e.target.value)} data-testid="rf-pickup" />
            {err.pickup_date && <p className="text-xs text-[#A11016] mt-1">{err.pickup_date}</p>}
          </div>
          <div>
            <Label className="text-sm mb-1 block">İade Tarihi *</Label>
            <Input type="date" min={f.pickup_date || minDate} value={f.return_date} onChange={(e) => set("return_date", e.target.value)} data-testid="rf-return" />
            {err.return_date && <p className="text-xs text-[#A11016] mt-1">{err.return_date}</p>}
          </div>
        </div>
        <div>
          <Label className="text-sm mb-1 block">Not (isteğe bağlı)</Label>
          <Textarea rows={2} value={f.note} onChange={(e) => set("note", e.target.value)} data-testid="rf-note" />
        </div>
        <div className="flex items-start gap-2">
          <Checkbox id="rf-kvkk" checked={f.kvkk} onCheckedChange={(v) => set("kvkk", !!v)} className="mt-0.5" data-testid="rf-kvkk" />
          <Label htmlFor="rf-kvkk" className="text-xs text-neutral-600 cursor-pointer leading-relaxed">KVKK Aydınlatma Metni'ni okudum ve kabul ediyorum. *</Label>
        </div>
        {err.kvkk && <p className="text-xs text-[#A11016]">{err.kvkk}</p>}
        <Button type="submit" className="w-full h-12 bg-[#A11016] hover:bg-[#820D12] text-white font-semibold gap-2" data-testid="rf-submit">
          <WhatsAppIcon className="h-4 w-4" /> WhatsApp ile Gönder
        </Button>
        <a href={`tel:${settings.phone_link}`} className="block text-center text-sm text-neutral-500 hover:text-[#A11016]" data-testid="rf-call">
          Veya hemen arayın: <span className="font-semibold">{settings.phone}</span>
        </a>
      </div>
    </form>
  );
}

function TechTable({ vehicle }) {
  const rows = [
    ["Kasa Tipi", vehicle.body_type],
    ["Yakıt Türü", vehicle.fuel === "benzin" ? "Benzin" : vehicle.fuel === "dizel" ? "Dizel" : "Hibrit"],
    ["Şanzıman", vehicle.transmission_label || "Otomatik"],
    ["Motor Hacmi", vehicle.engine],
    ["Motor Gücü", vehicle.power],
    ["Koltuk Sayısı", vehicle.seats ? `${vehicle.seats} Kişilik` : ""],
    ["Kapı Sayısı", vehicle.doors ? `${vehicle.doors} Kapı` : ""],
    ["Bagaj Hacmi", vehicle.luggage_l],
    ["Klima", vehicle.ac ? "Var" : "Yok"],
  ].filter((r) => r[1]);

  return (
    <div className="bg-white rounded-2xl border border-neutral-200 overflow-hidden" data-testid="tech-specs-table">
      <div className="px-5 py-4 border-b border-neutral-200"><h2 className="font-bold text-[#171717]">Teknik Özellikler</h2></div>
      <div>
        {rows.map(([k, v], i) => (
          <div key={k} className={`flex justify-between items-center px-5 py-3 ${i % 2 === 1 ? "bg-[#F5F5F7]" : "bg-white"}`}>
            <span className="text-sm text-neutral-500">{k}</span>
            <span className="text-sm font-semibold text-[#171717] text-right">{v}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function VehicleHeader({ vehicle }) {
  const { settings } = useSettings();
  const chips = [vehicle.body_type, vehicle.fuel === "benzin" ? "Benzin" : vehicle.fuel === "dizel" ? "Dizel" : "Hibrit", vehicle.transmission_label || "Otomatik", `${vehicle.seats} Koltuk`].filter(Boolean);
  return (
    <div>
      <div className="text-sm font-semibold text-[#A11016]">{vehicle.brand}</div>
      <h1 className="text-2xl sm:text-3xl font-bold text-[#171717]">{vehicle.name}</h1>
      <div className="flex flex-wrap gap-2 mt-3">
        {chips.map((c) => <span key={c} className="px-3 py-1 rounded-full bg-[#F5F5F7] text-xs font-medium text-neutral-600">{c}</span>)}
      </div>
      <div className="mt-5">
        <a href={`tel:${settings.phone_link}`} className="inline-flex items-center gap-2 text-2xl font-bold text-[#A11016] hover:text-[#820D12]" data-testid="detail-call-price">
          <Phone className="h-6 w-6" /> Fiyat için arayın
        </a>
        <p className="text-xs text-neutral-400 mt-2 max-w-md">En güncel fiyat ve müsaitlik bilgisi için bizimle iletişime geçin.</p>
      </div>
    </div>
  );
}

export default function VehicleDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [vehicle, setVehicle] = useState(null);
  const [similar, setSimilar] = useState([]);
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
    setActiveImg(0);
    api.get(`/vehicles/${slug}`).then((r) => {
      setVehicle(r.data);
      document.title = `${r.data.name} Kiralama | VoyaCar Rental`;
      api.get("/vehicles", { params: { segment: r.data.segment } }).then((s) => {
        setSimilar(s.data.filter((v) => v.slug !== slug).slice(0, 3));
      });
    }).catch(() => navigate("/araclar"));
  }, [slug, navigate]);

  if (!vehicle) return <div className="min-h-[60vh] flex items-center justify-center"><div className="h-10 w-10 border-4 border-[#A11016] border-t-transparent rounded-full animate-spin" /></div>;

  const hasImage = (vehicle.images || []).length > 0;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-10">
      <nav className="flex items-center gap-1.5 text-sm text-neutral-500 mb-6" data-testid="breadcrumb">
        <Link to="/" className="hover:text-[#A11016]">Anasayfa</Link>
        <ChevronRight className="h-4 w-4" />
        <Link to="/araclar" className="hover:text-[#A11016]">Araçlar</Link>
        <ChevronRight className="h-4 w-4" />
        <span className="text-[#171717] font-medium">{vehicle.name}</span>
      </nav>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-8">
        {/* LEFT */}
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
            <div className="bg-[#F5F5F7] rounded-2xl p-4">
              {hasImage && (
                <>
                  <div className="flex items-center justify-center min-h-[220px]">
                    <img src={vehicle.images[activeImg]} alt={vehicle.name} className="w-full h-auto object-contain" data-testid="vehicle-main-image" />
                  </div>
                  {vehicle.images.length > 1 && (
                    <div className="flex gap-2 mt-3 overflow-x-auto">
                      {vehicle.images.map((img, i) => (
                        <button
                          key={i}
                          onClick={() => setActiveImg(i)}
                          data-testid={`vehicle-thumb-${i}`}
                          className={`h-14 w-20 shrink-0 rounded-lg overflow-hidden border-2 bg-white ${activeImg === i ? "border-[#A11016]" : "border-transparent"}`}
                        >
                          <img src={img} alt="" className="w-full h-full object-contain" />
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
            {/* header on mobile shows below image via order; here render for desktop right of image */}
            <div className="order-2"><VehicleHeader vehicle={vehicle} /></div>
          </div>

          {/* Reservation form appears here on mobile (below header) */}
          <div className="lg:hidden"><ReservationForm vehicle={vehicle} /></div>

          <TechTable vehicle={vehicle} />

          {vehicle.description?.length > 0 && (
            <div className="space-y-3" data-testid="vehicle-description">
              {vehicle.description.map((p, i) => (
                <p key={i} className="text-neutral-600 leading-relaxed">{p}</p>
              ))}
            </div>
          )}
        </div>

        {/* RIGHT — sticky form (desktop only) */}
        <div className="hidden lg:block">
          <div className="sticky top-6">
            <ReservationForm vehicle={vehicle} />
          </div>
        </div>
      </div>

      {similar.length > 0 && (
        <div className="mt-16">
          <h2 className="text-2xl font-bold text-[#171717] mb-6">Benzer Araçlar</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {similar.map((v, i) => <VehicleCard key={v.id} vehicle={v} index={i} />)}
          </div>
        </div>
      )}
    </div>
  );
}
